import classnames from 'classnames'; // For managing active tab classes
import JsonToTable from 'components/JsonToTable';
import LoadingScreen from 'components/LoadingScreen';
import ModalDialog from 'components/ModalDialog';
import { useAtomValue } from 'jotai';
import callApi from 'lib/callApi';
import { isAdminUserLoggedIn } from 'lib/helper';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React, { useState } from 'react';
import {
  Button,
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from 'reactstrap';
import { companyIdAtom } from 'views/state';

interface LogEntry {
  file: string;
  line: string;
  message: string;
}

export enum ScannerType {
  DotNet = 'SonarScanner for MSBuild',
  Java = 'Java',
}

interface BuildOutput {
  errors: LogEntry[];
  warnings: LogEntry[];
  error_count: number;
  warning_count: number;
}

interface BuildLog {
  project: string;
  build_output: BuildOutput;
  scanner_type: string;
}

interface BuildLogsData {
  build_logs: BuildLog[];
  build_number: string;
}

interface IProps {
  repositoryId: number; // New prop to pass repository ID: number;
  showWarnings?: boolean; // New prop to optionally render warnings
}

export const BuildLogsTable: React.FC<IProps> = ({
  repositoryId,
  showWarnings = false,
}) => {
  const [logsData, setLogsData] = useState<BuildLogsData | null>(null); // State to hold build logs data
  const [isLoading, setIsLoading] = useState(true); // State to track loading state
  const companyId = useAtomValue(companyIdAtom);

  React.useEffect(() => {
    const fetchBuildLogs = async () => {
      setIsLoading(true);
      const params = {
        company_id: companyId,
      };
      const url = `sast/repositories/${repositoryId}/build-logs/`;
      await callApi(url, 'GET', params)
        .then((res) => {
          if (res.data) {
            setLogsData(res.data.last_build_logs); // Update state with fetched build logs data
          }
        })
        .catch((err) => {
          let res = err.response;
          showFailureToast(
            `Failed to fetch build logs: ${res?.status} - ${
              res?.data?.message || 'Unknown error'
            }`,
          ); // Show failure toast if there's an error // Show failure toast if there's an error
        })
        .finally(() => {
          setIsLoading(false); // Set loading state to false after fetching
        });
    };
    fetchBuildLogs();
  }, [repositoryId, companyId]);
  const hasLogs =
    logsData && logsData.build_logs && logsData.build_logs.length > 0;

  const [activeTab, setActiveTab] = useState(0); // Track active tab

  const toggleTab = (tabIndex: number) => {
    if (activeTab !== tabIndex) setActiveTab(tabIndex);
  };

  if (isLoading) {
    return (
      <div style={{ padding: '10px', height: '100%', minHeight: '400px' }}>
        <div className="masking-layer">
          <LoadingScreen mask={true} />
        </div>
      </div>
    );
  }

  if (!hasLogs) {
    return (
      <p style={{ color: 'gray', marginBottom: '20px' }}>No logs available</p>
    );
  }

  const handleDownload = (log) => {
    const blob = new Blob([log], { type: 'text/plain' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'build_output.txt';
    link.click();
    showSuccessToast('Downloaded build output');
  };

  const handleCopy = (log) => {
    navigator.clipboard.writeText(log).then(() => {
      showSuccessToast('Copied to clipboard');
    });
  };

  return (
    <div style={{ padding: '10px' }}>
      <h2
        style={{
          marginBottom: '20px',
          fontSize: '1.5rem',
          color: 'red',
        }}
      >
        Build Failed for one or more projects.
      </h2>
      <p style={{ color: 'red', marginBottom: '20px' }}>
        One or more projects in your repository failed to build, which prevents
        the scanning process from proceeding.
      </p>
      <p style={{ color: 'red', marginBottom: '20px' }}>
        Building the project (e.g., generating DLLs for .NET or JARs for Java)
        is required before scanning for issues. Please review the build errors
        in the tabs below, correct the issues, and re-trigger the build. Once
        the build is successful, the scanning process will automatically
        continue.
      </p>

      {/* Nav for tabs */}
      <Nav tabs>
        {logsData.build_logs.map((_, index) => (
          <NavItem key={index}>
            <NavLink
              className={classnames({ active: activeTab === index })}
              onClick={() => toggleTab(index)}
              style={{ cursor: 'pointer', fontWeight: 'bold' }}
            >
              {`Project ${index + 1}`}
            </NavLink>
          </NavItem>
        ))}
      </Nav>

      {/* Tab content */}
      <TabContent activeTab={activeTab}>
        {logsData.build_logs.map((log, index) => {
          const { project, scanner_type, build_output } = log || {
            project: '',
            scanner_type: '',
            build_output: {
              errors: [],
              warnings: [],
              error_count: 0,
              warning_count: 0,
            },
          };
          const { errors, error_count, warnings, warning_count } = build_output;

          return (
            <TabPane key={index} tabId={index}>
              <br />
              <div className="labels" style={{ margin: '15px' }}>
                <label style={{ marginRight: '15px' }}>
                  <strong>Project File:</strong> {project}
                </label>
                {isAdminUserLoggedIn() && (
                  <>
                    <br />
                    <label style={{ marginRight: '15px' }}>
                      <strong>Scanner:</strong> {scanner_type}
                    </label>
                  </>
                )}
                <br />
                {errors.length > 0 && errors.length === error_count && (
                  <label style={{ marginRight: '15px' }}>
                    <strong>Error Count:</strong> {error_count}
                  </label>
                )}
                {showWarnings && (
                  <>
                    <br />
                    <label style={{ marginRight: '15px' }}>
                      <strong>Warning Count:</strong> {warning_count}
                    </label>
                  </>
                )}
              </div>
              <h3
                style={{
                  color: 'red',
                  marginTop: '10px',
                  marginBottom: '0px',
                }}
              >
                {errors.length > 0 && errors.length === error_count
                  ? 'Errors'
                  : 'Build Output'}
              </h3>
              {(!errors.length || errors.length !== error_count) && (
                <p>
                  Preview the build output and fix the errors to scan this
                  projects.
                </p>
              )}
              {errors?.length > 0 && errors.length === error_count ? (
                <JsonToTable
                  data={errors}
                  cssClasses="table-errors"
                  cssStyles={{
                    border: '1px solid red',
                    borderRadius: '5px',
                    marginBottom: '20px',
                    backgroundColor: '#ffe6e6',
                  }}
                />
              ) : (
                <div>
                  <div
                    style={{
                      maxHeight: '300px',
                      overflowY: 'auto',
                      border: '1px solid #ccc',
                      borderRadius: '5px',
                      padding: '10px',
                      // backgroundColor: '#f9f9f9',
                      marginBottom: '20px',
                    }}
                  >
                    <pre
                      style={{
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        fontSize: '12px',
                      }}
                    >
                      {log['raw_build_output']}
                    </pre>
                  </div>
                  <Row
                    style={{ justifyContent: 'flex-end', marginBottom: '20px' }}
                  >
                    <Col xs="auto">
                      <Button
                        color="secondary"
                        onClick={() => handleCopy(log['raw_build_output'])}
                      >
                        Copy to Clipboard
                      </Button>
                    </Col>
                    <Col xs="auto">
                      <Button
                        color="primary"
                        onClick={() => handleDownload(log['raw_build_output'])}
                        style={{ marginRight: '10px' }}
                      >
                        Download
                      </Button>
                    </Col>
                  </Row>
                </div>
              )}

              {showWarnings && (
                <>
                  <h3
                    style={{
                      color: 'orange',
                      marginTop: '10px',
                      marginBottom: '0px',
                    }}
                  >
                    Warnings
                  </h3>
                  {warnings?.length > 0 ? (
                    <JsonToTable
                      data={warnings}
                      cssClasses="table-warnings"
                      cssStyles={{
                        border: '1px solid orange',
                        borderRadius: '5px',
                        marginBottom: '20px',
                        backgroundColor: '#fff3cd',
                      }}
                    />
                  ) : (
                    <p style={{ color: 'gray', marginBottom: '20px' }}>
                      No warnings found
                    </p>
                  )}
                </>
              )}
            </TabPane>
          );
        })}
      </TabContent>
    </div>
  );
};

const BuildLogsDisplayDialog: React.FC<IProps> = ({
  repositoryId,
  showWarnings = false,
}) => {
  const [isModalOpen, setModalOpen] = useState(false);

  // Handle opening and closing of the modal
  const toggleModal = () => setModalOpen(!isModalOpen);

  return (
    <>
      {/* Display icon if logs are present */}

      <i
        className="fas fa-exclamation-triangle fa-lg"
        style={{ color: 'red', cursor: 'pointer', margin: '10px' }}
        onClick={toggleModal}
        title="View Scan errors..."
      ></i>

      {/* Display modal dialog with build logs when the icon is clicked */}
      {isModalOpen && (
        <ModalDialog
          title="Scan Errors"
          onClose={toggleModal}
          childComponent={
            <BuildLogsTable
              repositoryId={repositoryId}
              showWarnings={showWarnings}
            />
          }
          customFooter={
            <button className="btn btn-primary" onClick={toggleModal}>
              Close
            </button>
          }
        />
      )}
    </>
  );
};

export default BuildLogsDisplayDialog;
